import { Moon, Sun, Monitor } from 'lucide-react';
import { useTheme, Theme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';

interface ThemeSelectorProps {
  /** Compacte weergave (alleen icons) */
  compact?: boolean;
  /** Extra CSS classes */
  className?: string;
}

interface ThemeOption {
  value: Theme;
  label: string;
  icon: React.ReactNode;
}

const themeOptions: ThemeOption[] = [
  { value: 'light', label: 'Licht', icon: <Sun className="w-4 h-4" /> },
  { value: 'dark', label: 'Donker', icon: <Moon className="w-4 h-4" /> },
  { value: 'system', label: 'Systeem', icon: <Monitor className="w-4 h-4" /> },
];

/**
 * ThemeSelector - Keuze tussen light/dark/system thema
 */
export function ThemeSelector({ compact = false, className }: ThemeSelectorProps) {
  const { theme, setTheme } = useTheme();

  return (
    <div className={cn('flex gap-1 p-1 rounded-lg bg-bg-input', className)}>
      {themeOptions.map((option) => {
        const isActive = theme === option.value;

        return (
          <button
            key={option.value}
            onClick={() => setTheme(option.value)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200',
              'min-h-touch focus:outline-none focus:ring-2 focus:ring-zzing-purple-500 focus:ring-offset-1',
              isActive
                ? 'bg-zzing-purple-500 text-white shadow-sm'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover'
            )}
            title={option.label}
            aria-pressed={isActive}
          >
            {option.icon}
            {!compact && <span>{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ThemeToggle - Simpele toggle knop tussen dark/light
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      className={cn(
        'p-2 rounded-lg transition-colors duration-200',
        'text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover',
        'focus:outline-none focus:ring-2 focus:ring-zzing-purple-500',
        className
      )}
      title={isDark ? 'Schakel naar licht thema' : 'Schakel naar donker thema'}
      aria-label={isDark ? 'Schakel naar licht thema' : 'Schakel naar donker thema'}
    >
      {isDark ? (
        <Sun className="w-5 h-5" />
      ) : (
        <Moon className="w-5 h-5" />
      )}
    </button>
  );
}

export default ThemeSelector;
